import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { createUseStyles } from 'react-jss'
import classNames from 'classnames'
import {
  TitleBar,
  CloseIcon,
  DownloadIcon,
  BoldArrowUpIcon,
  BoldArrowDownIcon,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CaretRightIcon,
  PrintIcon
} from '@okcredit/web-lib'
import PDFIcon from 'shared/assets/icons/icon-pdf.webp'
import DateFilter from 'components/DateFilter'
import { OVERALL_FILTER, IDateFilter } from 'components/DateFilter/DateFilter.constants'
import { THEME } from 'components/theme'
import ThreeDotsComponent from 'pages/CommonSummary/components/ThreeDotsComponent'
import HeaderRightContainer from 'pages/NewDashboard/components/Layout/headerRightContainer'
import { IAppState } from 'infra/AppState'
import { IMerchantProfile } from 'state/dashboard/dashboard.types'
import { formatCurrency } from 'utils/formatCurrency'
import { TRANSACTION_CATEGORY, TRANSACTION_TYPE } from 'state/transaction/transaction.types'
import { getTransactionDatabaseManager } from 'services/IndexDB/TransactionDatabaseManager'
import { refreshMerchantTransactions } from 'services/TransactionSync/refreshMerchantTransactions'
import {
  customerTransactionTableColumns,
  supplierTransactionTableColumns,
  formatBillDate
} from 'pages/Transactions/components/TransactionReport/TransactionReport.helper'
import TransactionReportStyles from 'pages/Transactions/components/TransactionReport/TransactionReport.styles'
import { ENTITY_TYPE } from '../../constants'
import { buildAccountReport, IAccountReport } from './accountReport.builder'
import { fetchAccountReportAccounts, IAccountReportAccountFull } from './accountReport.accounts'
import {
  downloadAccountReportExcel,
  downloadAccountReportPdf,
  printAccountReportPdf
} from './accountReport.export'
import { AccountReportStyles } from './AccountReport.styles'

const reportStyles = createUseStyles(TransactionReportStyles)

// Skeleton loading placeholders — sized per column (Date | Account | Notes | Payments |
// Credits | Balance) so they echo the real table's proportions while syncing. Rows are
// generous so the shimmer fills a tall viewport top-to-bottom; the skeleton area is
// overflow-hidden, so any extra rows past the fold are simply clipped (no scrollbar).
const SKELETON_ROWS = 18
const SKELETON_HEAD_WIDTHS = [46, 90, 54, 58, 56, 50]
const SKELETON_BODY_WIDTHS = [70, 130, 92, 64, 64, 74]
// Deterministic per-cell width variation (no Math.random) so rows don't look like a
// rigid grid; each column keeps its own base width.
const SKELETON_JITTER = [1, 0.82, 0.94, 0.72, 0.88]
const skeletonBodyWidth = (col: number, row: number): number => {
  const base = SKELETON_BODY_WIDTHS[col] ?? 60
  return Math.round(base * SKELETON_JITTER[(row + col) % SKELETON_JITTER.length])
}

interface IAccountReportProps {
  entityType: ENTITY_TYPE
  onClose: () => void
}

type Phase = 'syncing' | 'ready' | 'error'

const AccountReport: React.FC<IAccountReportProps> = ({ entityType, onClose }) => {
  const classes = reportStyles()
  const local = AccountReportStyles()
  const dialogRef = useRef<{ resetDialog: () => void }>(null)
  const isCustomer = entityType === ENTITY_TYPE.CUSTOMER
  const tableColumns = isCustomer
    ? customerTransactionTableColumns
    : supplierTransactionTableColumns
  // Consolidated report shows Account (name) and Notes as SEPARATE columns (the shared
  // column set has one Notes slot): Date | Account | Notes | Payments | Credits | Balance.
  const reportColumns = tableColumns.flatMap((c) =>
    c.id === 'note'
      ? [
          { id: 'account', label: 'Account' },
          { id: 'note', label: 'Notes' }
        ]
      : [c]
  )

  const merchantProfile: IMerchantProfile | undefined = useSelector(
    (app: IAppState) => app.Dashboard.merchantProfile?.data
  )

  const [phase, setPhase] = useState<Phase>('syncing')
  const [progress, setProgress] = useState({ completed: 0, total: 0 })
  const [accounts, setAccounts] = useState<IAccountReportAccountFull[]>([])
  const [transactionsByAccount, setTransactionsByAccount] = useState<Record<string, any[]>>({})
  const [filter, setFilter] = useState<IDateFilter>(OVERALL_FILTER)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showToTop, setShowToTop] = useState(false)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setPhase('syncing')
      try {
        const fullAccounts = await fetchAccountReportAccounts(entityType)
        if (!cancelled) {
          setAccounts(fullAccounts)
        }
        // Freshen the cache with ONE merchant delta (type:0) that updates every
        // account at once, then build the report straight from IndexedDB — instead
        // of a per-account sync (1 + N calls). Best-effort: a refresh failure still
        // renders the report from whatever is already cached (offline-friendly).
        setProgress({ completed: 0, total: 1 })
        try {
          await refreshMerchantTransactions()
        } catch (refreshErr) {
          console.warn('[AccountReport] delta refresh failed; using cached data:', refreshErr)
        }
        if (!cancelled) {
          setProgress({ completed: 1, total: 1 })
        }
        const dbManager = getTransactionDatabaseManager()
        await dbManager.init()
        const grouped = await dbManager.getTransactionsByType(entityType)
        if (!cancelled) {
          setTransactionsByAccount(grouped)
          setPhase('ready')
        }
      } catch (e) {
        if (!cancelled) {
          setPhase('error')
        }
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [entityType])

  const report: IAccountReport = useMemo(
    () =>
      buildAccountReport({
        entityType,
        accounts,
        transactionsByAccount,
        filter: { startTime: filter.startTime, endTime: filter.endTime }
      }),
    [entityType, accounts, transactionsByAccount, filter]
  )

  const business = {
    name: merchantProfile?.name || '',
    mobile: merchantProfile?.mobile || '',
    address: merchantProfile?.address || ''
  }
  const exportArgs = {
    report,
    filter: { startTime: filter.startTime, endTime: filter.endTime, label: filter.label },
    business
  }
  const canExport = phase === 'ready' && report.transactions.length > 0

  const onDownloadExcel = () => {
    if (canExport) {
      downloadAccountReportExcel(exportArgs)
    }
    dialogRef.current?.resetDialog()
  }
  const onDownloadPDF = () => {
    if (canExport) {
      downloadAccountReportPdf(exportArgs)
    }
    dialogRef.current?.resetDialog()
  }
  const onPrint = () => {
    if (canExport) {
      printAccountReportPdf(exportArgs)
    }
    dialogRef.current?.resetDialog()
  }

  const listItems = [
    {
      icon: <img src={PDFIcon} alt="pdf" height={22} width={22} />,
      title: 'Download PDF',
      endAdornment: <DownloadIcon size={22} fill={THEME.brandTintGreen} />,
      clickHandler: onDownloadPDF
    },
    {
      icon: <PrintIcon size={22} fill={THEME.brandBlack} />,
      title: 'Print This Page',
      endAdornment: <CaretRightIcon size={22} fill={THEME.brandTintGreen} />,
      clickHandler: onPrint
    }
  ]

  // Aggregate opening/closing for the merged report (mirrors TransactionReport's
  // uniform formula: closing = current net balance, opening = closing net of the
  // filtered window's payments/credits).
  const closingBalance = report.combinedBalance
  // Discounts affect the balance like payments, so they're netted out here too even
  // though they're excluded from the Total Payments figure.
  const openingBalance = report.combinedBalance - report.payment - report.discount + report.credit

  const title = `${isCustomer ? 'All Customers' : 'All Suppliers'} Report`
  const secondary =
    phase === 'syncing'
      ? `Syncing ${progress.completed} / ${progress.total || '…'} accounts`
      : `Net Balance ₹ ${formatCurrency(Math.abs(report.combinedBalance))} ${
          report.combinedBalance < 0 ? 'Due' : 'Advance'
        } · ${report.accountsCount} Accounts`

  const renderLedgerRows = () =>
    report.transactions.length > 0 ? (
      report.transactions.map((row) => {
        const isPayment = row.transactionType === TRANSACTION_TYPE.PAYMENT
        const amountCell = (show: boolean, className: string) =>
          show ? (
            <>
              <div className={className}>{`₹ ${formatCurrency(row.amount)}`}</div>
              {row.collectionId ? (
                <div className={classes.paymentType}>Online Payment</div>
              ) : row.transactionCategory === TRANSACTION_CATEGORY.DISCOUNT ? (
                <div className={classes.paymentType}>Discount offered</div>
              ) : null}
            </>
          ) : null
        return (
          <TableRow key={row.id} className={local.ledgerRow}>
            <TableCell
              className={classNames(classes.tableCell, classes.dateCell, classes.smallTableCell)}
            >
              {formatBillDate(Number(row.billedTime))}
            </TableCell>
            <TableCell className={classNames(classes.tableCell, classes.notesCell)}>
              <span className={local.rowAccountName}>
                {row.accountName || '—'}
                {row.accountDeleted && <span className={local.deletedTag}>Deleted</span>}
              </span>
            </TableCell>
            <TableCell className={classNames(classes.tableCell, classes.notesCell)}>
              {row.note || ''}
            </TableCell>
            <TableCell className={classes.smallTableCell}>
              {isCustomer
                ? amountCell(isPayment, classes.paymentText)
                : amountCell(!isPayment, classes.creditText)}
            </TableCell>
            <TableCell className={classes.smallTableCell}>
              {isCustomer
                ? amountCell(!isPayment, classes.creditText)
                : amountCell(isPayment, classes.paymentText)}
            </TableCell>
            <TableCell
              className={classNames(classes.tableCell, classes.paymentText, {
                [classes.creditText]: row.outStandingAmount < 0
              })}
            >
              {`₹ ${formatCurrency(Math.abs(row.outStandingAmount))}`}
            </TableCell>
          </TableRow>
        )
      })
    ) : (
      <tr>
        <td colSpan={reportColumns.length} className={classes.noTransactionCell} align="center">
          No transactions were recorded during the selected period
        </td>
      </tr>
    )

  return (
    <div className={local.container}>
      <TitleBar
        className={classes.titleBar}
        startIcon={<CloseIcon size={24} />}
        onStartIconClick={onClose}
        primary={title}
        secondary={secondary}
        endAdornment={
          <HeaderRightContainer
            childOne={
              <Button
                endIcon={<DownloadIcon fill={THEME.brandGreen} size={20} />}
                variant="text"
                onClick={onDownloadExcel}
                disabled={!canExport}
              >
                Download as Excel
              </Button>
            }
            childTwo={<ThreeDotsComponent listItem={listItems} ref={dialogRef} />}
          />
        }
      />

      {phase === 'syncing' && (
        <div
          className={local.skeletonArea}
          aria-busy="true"
          aria-live="polite"
          aria-label="Loading report"
        >
          <Table hasStickyHeader>
            <TableHead>
              <TableRow>
                {reportColumns.map((column, ci) => (
                  <TableCell key={column.id} className={classes.tableHeadCell}>
                    <span
                      className={local.skBar}
                      style={{ width: SKELETON_HEAD_WIDTHS[ci] ?? 56 }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody style={{ backgroundColor: THEME.brandWhite }}>
              {Array.from({ length: SKELETON_ROWS }).map((_, ri) => (
                <TableRow key={ri} className={local.skeletonRow}>
                  {reportColumns.map((column, ci) => (
                    <TableCell key={column.id} className={classes.tableCell}>
                      <span className={local.skBar} style={{ width: skeletonBodyWidth(ci, ri) }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {phase === 'error' && (
        <div className={local.center}>
          <span className={local.muted}>Something went wrong. Please try again.</span>
        </div>
      )}

      {phase === 'ready' && (
        <>
          <div style={{ padding: '12px 20px' }}>
            <DateFilter activeFilterLabel={filter.label || ''} onClick={setFilter} />
            <div className={classes.summaryContainer}>
              <div className={classNames(classes.summaryBox, classes.due)}>
                <div className={classes.summaryContent}>
                  <span>Opening Balance</span>
                  <span
                    className={classes.amount}
                    style={{
                      color: openingBalance >= 0 ? THEME.brandGreen : THEME.brandDarkRed
                    }}
                  >
                    ₹ {formatCurrency(Math.abs(openingBalance))}
                  </span>
                </div>
              </div>
              <div className={classNames(classes.summaryBox, classes.payment)}>
                <BoldArrowDownIcon
                  fill={isCustomer ? THEME.brandGreen : THEME.brandDarkRed}
                  size={20}
                />
                <div className={classes.summaryContent}>
                  {isCustomer ? (
                    <>
                      <span>Total Payments ({report.paymentCount})</span>
                      <span className={classes.customerAmount}>
                        ₹ {formatCurrency(report.payment)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span>Total Credits ({report.creditCount})</span>
                      <span className={classes.supplierAmount}>
                        ₹ {formatCurrency(report.credit)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className={classNames(classes.summaryBox, classes.credit)}>
                <BoldArrowUpIcon
                  fill={isCustomer ? THEME.brandDarkRed : THEME.brandGreen}
                  size={20}
                />
                <div className={classes.summaryContent}>
                  {isCustomer ? (
                    <>
                      <span>Total Credits ({report.creditCount})</span>
                      <span className={classes.customerAmount}>
                        ₹ {formatCurrency(report.credit)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span>Total Payments ({report.paymentCount})</span>
                      <span className={classes.supplierAmount}>
                        ₹ {formatCurrency(report.payment)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className={classNames(classes.summaryBox, classes.due)}>
                <div className={classes.summaryContent}>
                  <span>Closing Balance</span>
                  <span
                    className={classes.amount}
                    style={{ color: closingBalance >= 0 ? THEME.brandGreen : THEME.brandDarkRed }}
                  >
                    ₹ {formatCurrency(Math.abs(closingBalance))}
                  </span>
                </div>
              </div>
            </div>
            {report.discount > 0 && (
              <div className={local.discountLine}>
                Discounts offered: ₹ {formatCurrency(report.discount)}
              </div>
            )}
          </div>

          <div
            ref={scrollRef}
            className={local.scrollArea}
            onScroll={(e) => setShowToTop((e.target as HTMLDivElement).scrollTop > 240)}
          >
            <Table hasStickyHeader>
              <TableHead>
                <TableRow>
                  {reportColumns.map((column) => {
                    const HeaderIcon = (column as { icon?: React.ElementType }).icon
                    return (
                      <TableCell key={column.id} className={classes.tableHeadCell}>
                        {HeaderIcon && (
                          <HeaderIcon
                            size={14}
                            style={{ marginRight: 4, verticalAlign: 'middle' }}
                          />
                        )}
                        <span>{column.label}</span>
                      </TableCell>
                    )
                  })}
                </TableRow>
              </TableHead>
              <TableBody style={{ backgroundColor: THEME.brandWhite }}>
                {renderLedgerRows()}
              </TableBody>
            </Table>
          </div>
          {showToTop && (
            <button
              type="button"
              className={local.toTop}
              onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              title="Scroll to top"
              aria-label="Scroll to top"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 19V5" />
                <path d="M6 11l6-6 6 6" />
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default AccountReport
